import Linkify from 'linkify-react';
import React from 'react';
import he from 'he';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useSessionStore } from '../stores/useSessionStore';

dayjs.locale('pt-br');

export const defaultAvatarUrl = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%23e0e0e0" d="M512 0C229.2 0 0 229.2 0 512s229.2 512 512 512 512-229.2 512-512S794.8 0 512 0z"/><path fill="%239e9e9e" d="M512 256c-88.4 0-160 71.6-160 160s71.6 160 160 160 160-71.6 160-160-71.6-160-160-160zm0 384c-141.4 0-256 71.6-256 160v64h512v-64c0-88.4-114.6-160-256-160z"/></svg>';

export const baseUrl = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8080/api'
  : 'https://chat.telein.com.br/api';

export const webhookBaseUrl = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_SERVER_WEBHOOK_URL
  : 'https://chat.telein.com.br/webhook';

export const webhookTeleinUrl = 'https://chat.telein.com.br/webhookTeleinIA';

export const sendTemplateMessageUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/enviarTemplate.php';

export const getTemplatesBaseUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/listarTemplates.php';

export const checkNumberUrl = 'https://chat.telein.com.br/proxy/paginas/urareversa/check_number.php';

export const getFromMsgByLidUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/busca_frommsg_com_lid.php';

export const baseMediaUrl = 'https://chat.telein.com.br/proxy/panfletointeligente/webhook/assinar_s3.php?mediaUrl=';

export const deleteMessageUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/deleteMensagem.php';

export const sendMessageUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/enviarMensagem.php';

export const sendMessageTestUrl = 'https://chat.telein.com.br/proxy/apis/enviomassa/enviarMensagem_gomes.php';

export const countryCodeToHtmlEmoji = {
  BR: '&#127463;&#127479;',
  US: '&#127482;&#127480;',
  ES: '&#127466;&#127480;',
  FR: '&#127467;&#127479;',
  DE: '&#127465;&#127466;',
  IT: '&#127470;&#127481;',
  JP: '&#127471;&#127477;',
  CN: '&#127464;&#127475;',
  RU: '&#127479;&#127482;',
  IN: '&#127470;&#127475;',
  GB: '&#127468;&#127463;',
  CA: '&#127464;&#127462;',
  AU: '&#127462;&#127482;',
  KR: '&#127472;&#127479;',
  MX: '&#127474;&#127485;',
  AR: '&#127462;&#127479;',
};

const getAccessToken = () => localStorage.getItem('accessToken');

const handleBlockingAuthError = (data) => {
  return useSessionStore.getState().handleBlockedSession(data);
};

export const refreshAuthToken = async () => {
  try {
    const response = await fetch(`${baseUrl}/users/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      let message;

      if (response?.message) {
        message = response.message;
      } else {
        message = response;
      }

      return { error: true, message };
    }

    const { accessToken } = await response.json();
    localStorage.setItem('accessToken', accessToken);
    return accessToken;
  } catch (error) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('User');
    window.location.href = '/login';
  }
};

const authRequest = async (url, options = {}) => {
  const token = getAccessToken();
  const user = JSON.parse(localStorage.getItem('User'));

  const headers = {
    'Content-Type': 'application/json',
    ...(user?.id && { 'X-User-ID': user.id }),
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    const newToken = await refreshAuthToken();

    if (newToken.error) {
      localStorage.removeItem('User');
      localStorage.removeItem('tempSession');
      localStorage.removeItem('accessToken');
      window.location.href = '/';
      return { error: true, message: 'UNAUTHORIZED' };
    }

    headers.Authorization = `Bearer ${newToken}`;
    return fetch(url, { ...options, headers, credentials: 'include' });
  }

  const data = await response.json();

  if (!response.ok) {
    if (handleBlockingAuthError(data)) {
      return {
        error: true,
        message: 'Usuário não ativo ou não possui recarga',
        ...data,
      };
    }

    let message;

    if (data?.message) {
      message = data.message;
    } else {
      message = data;
    }

    return { error: true, message, ...data };
  }

  return data;
};

const uploadWithProgress = async (url, formData, token, onProgress, customHeaders = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        onProgress(percentCompleted);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          resolve(xhr.responseText);
        }
      } else {
        reject(new Error(`Request failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };

    xhr.open('POST', url, true);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    Object.entries(customHeaders).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.send(formData);
  });
};

export const postRequest = (url, body) => authRequest(url, {
  method: 'POST',
  body: JSON.stringify(body),
});

export const sendMessageRequest = async (
  token,
  message,
  phoneNumber,
  mediaUrl,
  sendMessageUrl,
  userId,
  chatId,
  setorId,
) => {
  const body = {
    token,
    mensagem: message,
    telefone: phoneNumber,
    // sendSocket: 1,
    ticketId: chatId,
    userId,
    setorId,
    chat_telein: true,
    ...(mediaUrl && { url: mediaUrl }),
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    redirect: 'follow',
  };

  try {
    const response = await fetch(sendMessageUrl, requestOptions);
    let data = await response.json();

    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    if (data?.error?.message) {
      return {
        error: true,
        message: data.error.message,
        details: data.error,
      };
    }

    if (!response.ok) {
      const message = data?.message || data?.error?.message || data;
      return { error: true, message, details: data?.error };
    }

    return data;
  } catch (error) {
    return { error: true, message: error.message };
  }
};

export const postFormDataRequest = async (url, formData, config = {}) => {
  try {
    let token = getAccessToken();

    if (config.onUploadProgress) {
      try {
        return await uploadWithProgress(
          url,
          formData,
          token,
          config.onUploadProgress,
          config.headers,
        );
      } catch (error) {
        if (error.message.includes('401')) {
          token = await refreshAuthToken();
          return await uploadWithProgress(
            url,
            formData,
            token,
            config.onUploadProgress,
            config.headers,
          );
        }
      }
    }

    let response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        ...(config.headers || {}),
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...config,
    });

    if (response.status === 401) {
      token = await refreshAuthToken();
      response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`,
        },
        ...config,
      });
    }

    const responseData = await response.json();

    if (!response.ok) {
      if (handleBlockingAuthError(responseData)) {
        return {
          error: true,
          message: 'Usuário não ativo ou não possui recarga',
          ...responseData,
        };
      }

      return {
        error: true,
        message: responseData?.message || 'Request failed',
        status: response.status,
      };
    }

    return responseData;
  } catch (error) {
    return {
      error: true,
      message: error.message || 'Network error or server unavailable',
      status: error.status,
    };
  }
};

export const getRequest = (url) => authRequest(url);

export const patchRequest = (url, body) => authRequest(url, {
  method: 'PATCH',
  body: JSON.stringify(body),
});

export const deleteRequest = (url) => authRequest(url, { method: 'DELETE' });

export const formatPhoneNumber = (phone) => {
  if (!phone) return;

  const cleanPhone = phone.split('@')[0];

  const hasPlus = cleanPhone.startsWith('+');
  const normalizedPhone = hasPlus ? cleanPhone.slice(1) : cleanPhone;

  const countryCode = normalizedPhone.slice(0, 2);
  const areaCode = normalizedPhone.slice(2, 4);
  const numberPart1 = normalizedPhone.slice(4, 8);
  const numberPart2 = normalizedPhone.slice(8);

  return `${hasPlus ? '+' : ''}${countryCode} (${areaCode}) ${numberPart1}-${numberPart2}`;
};

export const fixEncodingStrings = (str) => {
  const isMalformed = /�|Ã|&[a-z]+;/.test(str);

  if (isMalformed) {
    const bytes = new Uint8Array([...str].map((char) => char.charCodeAt(0)));
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  }

  return str;
};

export const removeAfterSecondAmpersand = (str) => {
  const firstAmpersandIndex = str.indexOf('&');
  const secondAmpersandIndex = str.indexOf('&', firstAmpersandIndex + 1);

  if (firstAmpersandIndex !== -1 && secondAmpersandIndex !== -1) {
    return str.slice(0, secondAmpersandIndex);
  }

  return str;
};

export const getSenderName = (fromMe, currentChat, bodyfile) => {
  const isGroupChat = currentChat?.fromMsg?.endsWith('@g.us');

  if (fromMe === 0) {
    if (isGroupChat) {
      const senderArr = bodyfile?.split('|');

      if (!senderArr) return formatPhoneNumber(currentChat.fromMsg);
      const senderName = senderArr[2];
      const fromMsg = senderArr.find((sender) => sender.endsWith('whatsapp.net'));
      return senderName !== '' ? senderName : formatPhoneNumber(fromMsg);
    }
    return currentChat.customName
      || currentChat.nome
      || formatPhoneNumber(currentChat.fromMsg);
  }
  return '';
};

export const getSenderDescription = (fromMe, currentChat, api, bodyfile) => {
  const isGroupChat = currentChat?.fromMsg?.endsWith('@g.us');

  if (isGroupChat) {
    const descriptionArr = bodyfile?.split('|');
    if ((descriptionArr && descriptionArr[2] !== '')) {
      const fromMsg = descriptionArr.find((description) => description.endsWith('whatsapp.net'));

      return formatPhoneNumber(fromMsg) ?? descriptionArr[2];
    }
    return '';
  }
  return currentChat.nome && fromMe === 0 && api !== 2
    ? formatPhoneNumber(currentChat.fromMsg)
    : '';
};

export const formatContactCardBody = (body, mediaType, api) => {
  if (mediaType !== 'contactMessage') return null;

  const nameMatch = body.match(/FN:([^\r\n]+)/);
  const contactName = nameMatch
    ? he.decode(nameMatch[1])
    : '';

  let phoneNumbers = [];

  if (api === 0) {
    const waidMatches = [...body.matchAll(/waid=(\d+)/g)];
    phoneNumbers = [...new Set(waidMatches.map((match) => match[1]))];
  } else if (api === 1) {
    const telMatches = [...body.matchAll(/TEL[^:]*:([^\r\n]+)/g)];
    phoneNumbers = [...new Set(telMatches.map((match) => {
      return match[1].replace(/[^\d]/g, '');
    }))];
  }

  return (
    <div>
      <h1>{contactName || 'Desconhecido'}</h1>
      <div>
        {phoneNumbers.length > 0 ? (
          phoneNumbers.map((number, index) => (
            <div key={index}>
              <span>
                {formatPhoneNumber(number)}
              </span>
            </div>
          ))
        ) : (
          <span>Nenhum telefone encontrado</span>
        )}
      </div>
    </div>
  );
};

export const formatMessageBody = (
  message,
  isDeleted = null,
  isInternalChat,
  highlightString = '',
) => {
  const regex = /^\*(.*?)\*:\s*/;
  const match = message.match(regex);

  const linkifyOptions = {
    target: '_blank',
    rel: 'noopener noreferrer',
    className: 'custom-linkify-link',
  };

  const formatTextWithLineBreaks = (text) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const highlightText = (text, highlight) => {
    if (!highlight) return formatTextWithLineBreaks(text);

    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) => (part.toLowerCase() === highlight.toLowerCase() ? (
      <span key={index} style={{ backgroundColor: '#FFCC22', fontSize: '16px' }}>{part}</span>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )));
  };

  const messageStyle = {
    textDecoration: isDeleted ? 'line-through' : 'none',
  };

  if (match) {
    const boldText = match[1];
    const remainingText = message.replace(regex, '');

    return (
      <>
        <i><strong>{`${boldText}:`}</strong></i>
        {((!isDeleted && !isInternalChat)
          || (isInternalChat && !isDeleted)
          || !isInternalChat) && (
            <p style={messageStyle}>
              <Linkify options={linkifyOptions}>
                {highlightText(remainingText, highlightString)}
              </Linkify>
            </p>
          )}
      </>
    );
  }

  return (
    <p style={messageStyle}>
      <Linkify options={linkifyOptions}>
        {highlightText(message, highlightString)}
      </Linkify>
    </p>
  );
};

export const stripPrefix = (idWithPrefix) => {
  const separatorIndex = idWithPrefix.indexOf('-');
  if (separatorIndex !== -1) {
    return idWithPrefix.slice(separatorIndex + 1);
  }
  return idWithPrefix;
};

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const ItemTypes = {
  CARD: 'CARD',
  CHAT: 'CHAT',
};

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

export const showNotification = (title, options) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, options);

    notification.onclick = () => {
      window.focus();

      if (window.parent) {
        window.parent.focus();
      }

      notification.close();

      if (options.onClick) {
        options.onClick();
      }
    };
  }
};

export const formatMessageDate = (dateString) => {
  const messageDate = dayjs(dateString);
  const today = dayjs().startOf('day');
  const yesterday = dayjs().subtract(1, 'day').startOf('day');

  if (messageDate.isSame(today, 'd')) {
    return `Hoje às ${messageDate.format('HH:mm')}`;
  } if (messageDate.isSame(yesterday, 'd')) {
    return `Ontem às ${messageDate.format('HH:mm')}`;
  }
  return messageDate.format('DD/MM/YYYY HH:mm');
};

export const getAlternativeFromMsg = (fromMsg, remoteJidAlt) => {
  if (fromMsg?.includes('@lid') && remoteJidAlt) {
    return formatPhoneNumber(remoteJidAlt);
  }
  return formatPhoneNumber(fromMsg);
};
